/**
 * Usage sidebar panel showing aggregated token usage, per-agent breakdown,
 * and tool call metrics across the conversation.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart3,
  X,
  ChevronDown,
  ChevronRight,
  Zap,
  Bot,
  Wrench,
  Puzzle,
} from 'lucide-react';
import type { UIMessage } from '../../types/chat';
import { useUsageMetrics } from '../../hooks/useUsageMetrics';
import type { AgentUsage, ToolUsage } from '../../types/usage';

function formatTokenCount(count: number): string {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return String(count);
}

function formatDuration(ms: number): string {
  if (ms <= 0) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  count?: number;
  children: React.ReactNode;
}

function CollapsibleSection({ title, icon, defaultOpen = true, count, children }: SectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-100 dark:border-gray-800">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
        {icon}
        <span>{title}</span>
        {count !== undefined && count > 0 && (
          <span className="ml-auto px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded">
            {count}
          </span>
        )}
      </button>
      {isOpen && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

function TokenBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function AgentRow({ agent, maxTokens }: { agent: AgentUsage; maxTokens: number }) {
  return (
    <div className="py-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-700 dark:text-gray-200 font-medium truncate max-w-[140px]">{agent.name}</span>
        <span className="text-gray-400 dark:text-gray-500">
          {agent.invocations}x &middot; avg {formatDuration(agent.avgDurationMs)}
        </span>
      </div>
      <TokenBar value={agent.totalTokens} max={maxTokens} color="bg-amber-500" />
      <div className="flex items-center gap-3 text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
        <span>{formatTokenCount(agent.inputTokens)} in</span>
        <span>{formatTokenCount(agent.outputTokens)} out</span>
        <span>{agent.llmCalls} calls</span>
      </div>
    </div>
  );
}

function ToolRow({ tool }: { tool: ToolUsage }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-xs">
      <div className="flex items-center gap-1.5 min-w-0">
        {tool.isSkillTool && (
          <Puzzle className="w-3 h-3 text-purple-500 flex-shrink-0" />
        )}
        <span className="text-gray-700 dark:text-gray-300 truncate">{tool.name}</span>
      </div>
      <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2">
        <span>{tool.invocations}x</span>
        <span>{formatDuration(tool.avgDurationMs)}</span>
      </div>
    </div>
  );
}

interface UsageSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  messages: UIMessage[];
  toolTimings: Map<string, { startedAt: number; completedAt?: number }>;
}

export function UsageSidebar({ isOpen, onClose, messages, toolTimings }: UsageSidebarProps) {
  const [width, setWidth] = useState(384);
  const [isResizing, setIsResizing] = useState(false);
  const [skillFilter, setSkillFilter] = useState<'all' | 'skills'>('all');
  const sidebarRef = useRef<HTMLDivElement>(null);

  const metrics = useUsageMetrics(messages, toolTimings);

  const maxAgentTokens = metrics.agents.length > 0
    ? Math.max(...metrics.agents.map((a) => a.totalTokens))
    : 0;

  const filteredTools = skillFilter === 'skills'
    ? metrics.tools.filter((t) => t.isSkillTool)
    : metrics.tools;

  const skillTools = metrics.tools.filter((t) => t.isSkillTool);

  // Group skill tools by prefix (e.g. "server/tool" -> "server")
  const skillGroups = new Map<string, ToolUsage[]>();
  for (const tool of skillTools) {
    const slashIdx = tool.name.indexOf('/');
    const prefix = slashIdx > 0 ? tool.name.slice(0, slashIdx) : tool.name;
    const group = skillGroups.get(prefix) ?? [];
    group.push(tool);
    skillGroups.set(prefix, group);
  }

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.max(280, Math.min(600, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (!isOpen) return null;

  const hasData = metrics.total.messageCount > 0 || metrics.tools.length > 0;

  return (
    <div
      ref={sidebarRef}
      className="flex flex-col h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 relative"
      style={{ width: `${width}px` }}
      data-testid="usage-sidebar"
    >
      {/* Resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-sky-400 transition-colors z-10"
        onMouseDown={handleMouseDown}
        style={{ backgroundColor: isResizing ? '#38bdf8' : 'transparent' }}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <BarChart3 size={18} className="text-amber-600" />
          <span className="font-medium text-gray-900 dark:text-white">Usage</span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
          title="Close sidebar"
        >
          <X size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {!hasData ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <BarChart3 className="w-10 h-10 text-gray-300 dark:text-gray-700 mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No usage data yet</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Send a message to see metrics</p>
          </div>
        ) : (
          <>
            {/* Total Usage */}
            <CollapsibleSection
              title="Total Usage"
              icon={<Zap className="w-3.5 h-3.5 text-amber-500" />}
              defaultOpen={true}
            >
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div>
                  <div className="text-gray-400">Input Tokens</div>
                  <div className="text-gray-700 dark:text-gray-200 font-medium text-sm">
                    {formatTokenCount(metrics.total.inputTokens)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">Output Tokens</div>
                  <div className="text-gray-700 dark:text-gray-200 font-medium text-sm">
                    {formatTokenCount(metrics.total.outputTokens)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">Total Tokens</div>
                  <div className="text-gray-700 dark:text-gray-200 font-medium text-sm">
                    {formatTokenCount(metrics.total.totalTokens)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">LLM Calls</div>
                  <div className="text-gray-700 dark:text-gray-200 font-medium text-sm">
                    {metrics.total.llmCalls}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">Total Duration</div>
                  <div className="text-gray-700 dark:text-gray-200 font-medium text-sm">
                    {formatDuration(metrics.total.totalDurationMs)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">Responses</div>
                  <div className="text-gray-700 dark:text-gray-200 font-medium text-sm">
                    {metrics.total.messageCount}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Agents */}
            {metrics.agents.length > 0 && (
              <CollapsibleSection
                title="Agents"
                icon={<Bot className="w-3.5 h-3.5 text-sky-500" />}
                defaultOpen={true}
                count={metrics.agents.length}
              >
                <div className="space-y-1">
                  {metrics.agents.map((agent) => (
                    <AgentRow key={agent.name} agent={agent} maxTokens={maxAgentTokens} />
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Tools */}
            {metrics.tools.length > 0 && (
              <CollapsibleSection
                title="Tools"
                icon={<Wrench className="w-3.5 h-3.5 text-amber-500" />}
                defaultOpen={true}
                count={metrics.tools.length}
              >
                {skillTools.length > 0 && (
                  <div className="flex gap-1 mb-2">
                    <button
                      onClick={() => setSkillFilter('all')}
                      className={`px-2 py-0.5 rounded text-xs transition-colors ${
                        skillFilter === 'all'
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                          : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                      }`}
                    >
                      All ({metrics.tools.length})
                    </button>
                    <button
                      onClick={() => setSkillFilter('skills')}
                      className={`px-2 py-0.5 rounded text-xs transition-colors ${
                        skillFilter === 'skills'
                          ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
                          : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                      }`}
                    >
                      Skills ({skillTools.length})
                    </button>
                  </div>
                )}
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredTools.map((tool) => (
                    <ToolRow key={tool.name} tool={tool} />
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Skills (grouped) */}
            {skillGroups.size > 0 && (
              <CollapsibleSection
                title="Skills"
                icon={<Puzzle className="w-3.5 h-3.5 text-purple-500" />}
                defaultOpen={false}
                count={skillGroups.size}
              >
                {Array.from(skillGroups.entries()).map(([prefix, tools]) => {
                  const totalInvocations = tools.reduce((s, t) => s + t.invocations, 0);
                  return (
                    <div key={prefix} className="mb-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-purple-600 dark:text-purple-400 font-medium">{prefix}</span>
                        <span className="text-gray-400 dark:text-gray-500">{totalInvocations} calls</span>
                      </div>
                      <div className="pl-3 border-l border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                        {tools.map((tool) => (
                          <ToolRow key={tool.name} tool={tool} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </CollapsibleSection>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default UsageSidebar;
